import { all, call, put, takeEvery } from 'redux-saga/effects';
import { fetchEntity } from '@classpass/saga-tools';
import { reportError } from 'helpers/error-logging';
import { userSettings, userSmsSubscriptionStatus as userSmsSubscriptionStatusEntity } from 'store/saga/entities/bound';
import { createErrorSnackbar } from 'components/Snackbar';
import { createInfoBanner } from 'components/Banner';
import snakeCase from 'lodash/snakeCase';
import { parseNumber } from 'helpers/core';
import { NOTIFICATION_TYPE } from './Notifications.constants';
import messages from './Notifications.messages';

export const UPDATE_NOTIFICATIONS = 'venuePortal/notifications/UPDATE_NOTIFICATIONS';
export const updateNotifications = (formValues, asyncFns) => ({ type: UPDATE_NOTIFICATIONS, formValues, asyncFns });

export function* doUpdateNotifications(client, action) {
  try {
    const {
      formValues: {
        userId,
        applicableVenueIds,
        currentReceiveNotifications,
        currentUserSmsSubscriptionStatus,
        currentUserPhoneNumber,
        receiveNotifications,
        userSmsSubscriptionStatus,
        userPhoneNumber,
      },
      asyncFns: { resolveSubmit },
    } = action;
    // Write explicit rows for every applicable venue, not just changed ones — venues without a
    // row fall back to the backend default (opt-in), so a save must self-heal that gap (ST-8959).
    const settings = Object.entries(applicableVenueIds).reduce((updates, [type, venueIds]) => {
      const enabledIds = currentReceiveNotifications[type] || [];
      venueIds.forEach(venueId => {
        updates.push({
          userId,
          venueId: parseNumber(venueId),
          settingName: snakeCase(type),
          settingValue: enabledIds.includes(venueId) ? 'yes' : 'no',
        });
      });
      return updates;
    }, []);

    // SMS stays diff-only: its single-select dropdown renders value[0], so a full write could
    // persist venue rows the user never saw.
    const smsType = NOTIFICATION_TYPE.CAN_RECEIVE_SMS;
    const currentSmsIds = currentReceiveNotifications[smsType] || [];
    const initialSmsIds = receiveNotifications[smsType] || [];
    currentSmsIds
      .filter(venueId => !initialSmsIds.includes(venueId))
      .forEach(venueId => {
        settings.push({
          userId,
          venueId: parseNumber(venueId),
          settingName: snakeCase(smsType),
          settingValue: 'yes',
        });
      });
    initialSmsIds
      .filter(venueId => !currentSmsIds.includes(venueId))
      .forEach(venueId => {
        settings.push({
          userId,
          venueId: parseNumber(venueId),
          settingName: snakeCase(smsType),
          settingValue: 'no',
        });
      });

    if (currentUserSmsSubscriptionStatus !== userSmsSubscriptionStatus || currentUserPhoneNumber !== userPhoneNumber) {
      // If user tries to delete phone number from input, disable, and save, we default to the initial phone value.
      // This prevents saving an empty string to the database and allows user to opt back in without re-entering phone number.
      // Check that phone number is not falsy to prevent submitting undefined number.
      if (!!currentUserPhoneNumber || !!userPhoneNumber) {
        const phone = currentUserPhoneNumber || userPhoneNumber;
        const countryCode = phone.substring(0, 2);
        const phoneNumber = phone.substring(2);
        const smsSettings = {
          countryCode,
          phoneNumber,
          optIn: currentUserSmsSubscriptionStatus,
        };
        yield call(fetchEntity, userSmsSubscriptionStatusEntity, [client, { smsSettings, userId }]);
      }
    }

    yield call(fetchEntity, userSettings, [client, { settings, userId }]);
    yield put(createInfoBanner(messages.updateSuccess));
    yield call(resolveSubmit);
  } catch (error) {
    const {
      asyncFns: { rejectSubmit },
    } = action;
    const msg = error.response?.data?.message || messages.updateError;
    yield all([put(createErrorSnackbar(msg, {}, { skipIntl: true })), call(rejectSubmit), call(reportError, error)]);
  }
}

export function* handleRouteEnter(client) {
  try {
    yield all([
      call(fetchEntity, userSettings, [client]),
      call(fetchEntity, userSmsSubscriptionStatusEntity, [client]),
    ]);
  } catch (error) {
    yield all([put(createErrorSnackbar(messages.loadError)), call(reportError, error)]);
  }
}

export function* watchActions(client) {
  yield takeEvery(UPDATE_NOTIFICATIONS, doUpdateNotifications, client);
}
